<template>
  <section class="p-6 flex flex-col gap-6">
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="$router.push('/')">
        Home
      </span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Languages</span>
    </nav>

    <h3 class="font-bold text-2xl">Languages</h3>

    <div class="flex items-center justify-between w-full mb-4">
      <input
        v-model="search"
        placeholder="Search by Arabic/English name or code..."
        class="border border-gray-300 rounded-md px-4 py-2 w-1/3"
      />
      <button
        @click="$router.push({ name: 'AddLanguage' })"
        class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
      >
        + Add Language
      </button>
    </div>

    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse bg-white rounded-lg shadow">
        <thead>
          <tr class="bg-gray-100 text-left text-sm font-semibold">
            <th class="px-4 py-2">Arabic Name</th>
            <th class="px-4 py-2">English Name</th>
            <th class="px-4 py-2">Code</th>
            <th class="px-4 py-2">Sort Order</th>
            <th class="px-4 py-2">Active</th>
            <th class="px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="lang in filteredLanguages"
            :key="lang.id"
            class="border-t hover:bg-gray-50 text-sm"
          >
            <td class="px-4 py-2">{{ lang.name_ar }}</td>
            <td class="px-4 py-2">{{ lang.name_en }}</td>
            <td class="px-4 py-2">{{ lang.code }}</td>
            <td class="px-4 py-2">{{ lang.sort_order ?? 0 }}</td>
            <td class="px-4 py-2">
              <span :class="lang.is_active ? 'text-green-600' : 'text-red-500'">
                {{ lang.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="px-4 py-2 text-center whitespace-nowrap">
              <button
                @click="$router.push({ name: 'EditLanguage', params: { id: lang.id } })"
                class="text-green-600 hover:underline mx-1"
              >
                Edit
              </button>
              <button
                @click="deleteLanguage(lang.id)"
                class="text-red-600 hover:underline mx-1"
              >
                Delete
              </button>
            </td>
          </tr>

          <tr v-if="filteredLanguages.length === 0">
            <td colspan="6" class="text-center py-4 text-gray-500">
              No languages found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import apiClient from "@/apiClient";

const languages = ref([]);
const search = ref("");

async function fetchLanguages() {
  try {
    const res = await apiClient.get("/languages");
    languages.value = res.data?.data?.languages || [];
  } catch (error) {
    console.error("Error fetching languages:", error);
    languages.value = [];
  }
}

const filteredLanguages = computed(() => {
  if (!search.value.trim()) return languages.value;
  const q = search.value.toLowerCase();
  return languages.value.filter((l) => {
    const ar = String(l.name_ar || "").toLowerCase();
    const en = String(l.name_en || "").toLowerCase();
    const code = String(l.code || "").toLowerCase();
    return ar.includes(q) || en.includes(q) || code.includes(q);
  });
});

async function deleteLanguage(id) {
  if (!confirm("Are you sure you want to delete this language?")) return;
  try {
    await apiClient.delete(`/languages/${id}`);
    await fetchLanguages();
  } catch (error) {
    console.error(error);
    alert("Failed to delete language.");
  }
}

onMounted(fetchLanguages);
</script>

<style scoped>
table {
  border-radius: 0.5rem;
  overflow: hidden;
}
</style>
